// ** React Imports
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import Grid from '@mui/material/Grid'
import { useTranslation } from 'src/utils/i18n'

// ** MUI Imports
import ListItem from '@mui/material/ListItem'
import Typography from '@mui/material/Typography'

// ** Store & Actions Imports
import { useDispatch, useSelector } from 'react-redux'
import {
  selectMessage,
  fetchUserProfile,
  removeSelectedChat,
  setMessageDateRange
} from 'src/store/apps/android-local-text-messages'

// ** Antdesign Imports
import { DatePicker } from 'antd'

// ** Chat App Components Imports
import { setCurrentTab } from 'src/store/apps/Common'
import Image from 'next/image'
import { openInNewTab } from 'src/utils/navigate'
import dayjs from 'dayjs'
import styles from 'styles/video.module.scss'
import NoData from 'src/components/NoData'
import ContentLoading from 'src/components/ContentLoading'
import RangePickerBox from 'src/components/RangePickerBox'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'

// 附件资源域名（与 src/@core/components/text-messages/messages-log.js 保持一致）
const fileBaseUrl = 'https://cos.spyx.com/'

//判断聊天内容的类型
const content = (item, t) => {
  try {
    if (typeof item.attachment === 'undefined' || item.attachment === null) {
      return <>{item.text}</>
    } else if (
      item.attachment.mimeType === 'video/quicktime' ||
      item.attachment.mimeType === 'video/mp4' ||
      item.attachment.mimeType === 'video/webm' ||
      item.attachment.mimeType === 'video/mpeg'
    ) {
      return (
        <div className={styles.videoContainer}>
          <video
            className={styles.video}
            controls={true}
            autoPlay={false}
            id='video'
            preload='none'
            loop={false}
            muted={false}
          >
            {t('can not be used Video')}
            <source src={`https://cos.spyx.com/${item.attachment.url}`} type='video/mp4' />
          </video>
        </div>
      )
    } else if (
      item.attachment.mimeType === 'application/pdf' ||
      item.attachment.mimeType === 'application/msword' ||
      item.attachment.mimeType === 'application/json' ||
      item.attachment.mimeType === 'application/xml' ||
      item.attachment.mimeType === 'application/zip' ||
      item.attachment.mimeType === 'application/octet-stream'
    ) {
      return (
        <Image
          src={`/images/files/${item.attachment.mimeType.split('/')[2] === 'pdf'
            ? 'pdf'
            : item.attachment.mimeType.split('/')[2] === 'zip'
              ? 'zip'
              : 'empty'
            }.png`}
          onClick={() => openInNewTab(`${fileBaseUrl}${item.attachment.url}`)}
          width={80}
          height={80}
          alt={'file'}
        />
      )
    } else if (
      item.attachment.mimeType === 'image/jpeg' ||
      item.attachment.mimeType === 'image/png' ||
      item.attachment.mimeType === 'image/gif'
    ) {
      return <Image src={`https://cos.spyx.com/${item.attachment.url}`} alt='img' width='150' height='150' />
    }
  } catch (error) {
    console.log(error)
  }
}

const TextMessages = () => {
  let Basic = window.localStorage.getItem('Basic')
  Basic = Basic == null || Basic == 0 ? true : false

  const { t } = useTranslation('common')

  // 当前页面
  const [currentPage, setCurrentPage] = useState(1)

  // 消息列表
  const [messageList, setMessageList] = useState([])

  //筛选过的消息列表
  const [dateMessageList, setDateMessageList] = useState([])

  const messageListDom = useRef(null)
  const messageContentDom = useRef(null)

  // ** Hooks
  const dispatch = useDispatch()

  const store = useSelector(state => state.androidLocalTextMessages)
  const fetchLoading = [true, 'true'].includes(store.fetchLoading)

  const storeCommon = useSelector(state => state.Common)

  const { RangePicker } = DatePicker
  const func1 = useRef()
  const func2 = useRef()

  const [messageIndex, setMessageIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true) //正在加载获取数据

  useEffect(() => {
    const title = t('Text message')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  // 设备切换或首次进入时拉取短信并重置本地 UI 状态
  useDeviceScopedInit(
    useCallback(() => {
      setCurrentPage(1)
      setMessageList([])
      setDateMessageList([])
      setMessageIndex(0)
      setIsLoading(true)
      dispatch(fetchUserProfile())
    }, [dispatch])
  )

  useEffect(() => {
    setMessageList([...store.list])
    setDateMessageList([...store.list])
    setIsLoading(false)
  }, [store.list])

  // 短信列表滚动到底部
  useEffect(() => {
    if (!messageListDom.current) return
    if (currentPage !== 1) return

    const dom = messageListDom.current
    if (func1.current) {
      dom.removeEventListener('scroll', func1.current)
    }

    func1.current = () => {
      const clientHeight = dom.clientHeight
      const scrollTop = dom.scrollTop
      const scrollHeight = dom.scrollHeight
      if (clientHeight + scrollTop === scrollHeight) {
        dispatch(fetchUserProfile())
      }
    }
    dom.addEventListener('scroll', func1.current)

    return () => {
      dom.removeEventListener('scroll', func1.current)
    }
  }, [currentPage, dispatch])

  // 短信内容滚动到底部
  useEffect(() => {
    if (!messageContentDom.current) return
    if (currentPage !== 2) return

    const dom = messageContentDom.current
    if (func2.current) {
      dom.removeEventListener('scroll', func2.current)
    }

    func2.current = () => {
      const clientHeight = dom.clientHeight
      const scrollTop = dom.scrollTop
      const scrollHeight = dom.scrollHeight
      if (clientHeight + scrollTop === scrollHeight) {
        dispatch(selectMessage())
      }
    }
    dom.addEventListener('scroll', func2.current)

    return () => {
      dom.removeEventListener('scroll', func2.current)
    }
  }, [currentPage, dispatch])

  const onClickMessage = (guid, item, index) => {
    setMessageIndex(index)
    if (JSON.parse(localStorage.getItem('smsItem')).guid == guid) {
      setCurrentPage(2)
    } else {
      dispatch(selectMessage(item))
      localStorage.setItem('smsItem', JSON.stringify(item))
      setCurrentPage(2)
    }
  }

  const [antdSelectedDates, setAntdSelectedDates] = useState([])
  const [selectedDateIndex, setSelectedDateIndex] = useState()

  //选择日期范围
  const handleDateChange = (dates, dateStrings) => {
    setAntdSelectedDates(dates)

    const picked = dates && dates.length === 2 ? dates : []
    setSelectedDateIndex(picked.length === 2 ? 2 : 1)

    // 把区间交给 store，由接口按日期重新取数（列表会随 store.list 自动刷新）
    dispatch(setMessageDateRange(picked))
    dispatch(fetchUserProfile())
  }

  return (
    <>
      <div className='Bxl:h-[calc(var(--vh2)*100-90px)] Bxls:Headers5:h-[calc(var(--vh2)*100-145px)] Header5:big:h-[calc(var(--vh2)*100-210px)] bigs:h-[calc(var(--vh2)*100-270px)]'>
        {fetchLoading ? (
          <ContentLoading />
        ) : (
        <Grid
          item
          container
          spacing={0}
          sx={{ background: 'white', borderRadius: '10px' }}
          style={{ boxShadow: '0px 0px 12px 0px #00000021' }}
          className='h-full'
        >
          {/* 左侧列表 */}

          <div
            className={`px-[15px] h-full overflow-auto w-[345px] sm:w-full border-r-[1px] border-[rgb(229, 231, 235)]  ${currentPage !== 1 ? 'sm:hidden' : 'block'
              }`}
            ref={messageListDom}
          >
            {/*时间选择器*/}
            {dateMessageList && (
              <div className='mt-[20px] mb-[10px]'>
                <RangePickerBox
                  value={antdSelectedDates}
                  onChange={handleDateChange}
                  onClear={() => {
                    setAntdSelectedDates([])
                    handleDateChange([])
                  }}
                />
              </div>
            )}

            {store.list && store.list.length > 0 ? (
              <>
                {dateMessageList.map((item, i) => (
                  <div
                    className={`w-full h-[56px] py-[10px] flex items-center px-2 border-b hover:cursor-pointer border-[#E5E7EB] ${messageIndex === i ? 'bg-[#e9e9eb] rounded-[10px]' : ''
                      }`}
                    key={i}
                    onClick={() => onClickMessage(item.guid, item, i)}
                  >
                    <div className='flex flex-1'>
                      <Image
                        src={item.avatar ? item.avatar : '/images/text-messages/avatar.svg'}
                        width={36}
                        height={36}
                        alt='avatar'
                        className='mr-[17px]'
                      ></Image>
                      <div className='flex flex-col justify-center flex-1 w-0'>
                        <div className='flex justify-between items-center flex-1'>
                          <span className='text-[#000] text-[14px] font-[600] '>{item.chatid}</span>
                          <span className='text-[#808080] text-[10px]'>
                            {/* 今天 */}
                            {new Date(item.snippetTimestamp).toDateString() === new Date().toDateString() && (
                              <>{dayjs(item.snippetTimestamp).format('hh:mmA')} &gt;</>
                            )}
                            {/* 不是今天 */}
                            {new Date(item.snippetTimestamp).toDateString() !== new Date().toDateString() && (
                              <>{dayjs(item.snippetTimestamp).format('YYYY/MM/DD')} &gt;</>
                            )}
                          </span>
                        </div>

                        <span className='text-[#808080] text-[12px] overflow-hidden text-ellipsis whitespace-nowrap flex-1'>
                          {item.snippet}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <ListItem>
                <Typography sx={{ color: 'text.secondary' }}>
                  {store.fetchLoading ? (
                    <ContentLoading />
                  ) : (
                    <div className='text-center mt-[65%]'>
                      {t('It appears theres no data to show here yet')}<br />
                      {t('If data exists it may take up to 1 hour to download and appear')}<br />
                      {t('We suggest checking back in a little while')}
                    </div>
                  )}
                </Typography>
              </ListItem>
            )}
          </div>

          <div className={`w-full flex-1 ${currentPage === 2 ? 'block' : 'sm:hidden'}`}>
            {/* 消息框 */}
            {store.selectedMessage && store.selectedMessage ? (
              <>
                <div className='w-full h-[80px] bg-[#F5F5F5] flex justify-between px-[14px] border-b border-[#DADADA] rounded-tr-[10px] sm:rounded-tl-[10px]'>
                  <Image
                    className='md:invisible'
                    src='/images/text-messages/return.svg'
                    width={20}
                    height={20}
                    alt='return'
                    onClick={() => setCurrentPage(1)}
                  ></Image>
                  <div className='flex flex-col justify-end items-center'>
                    <Image src='/images/text-messages/avatar.svg' width={44} height={44} alt='alt'></Image>
                    <span className='text-[10px] text-[#000] my-[3px]'>{store.selectedMessage.contact.chatid}</span>
                  </div>
                  <div className='w-[20px] h-[20px]'></div>
                </div>
                <main
                  className='flex flex-col overflow-auto px-[14px] pb-[12px] Bxl:h-[calc(var(--vh2)*100-170px)] Bxls:Headers5:h-[calc(var(--vh2)*100-225px)] Header5:big:h-[calc(var(--vh2)*100-290px)] bigs:h-[calc(var(--vh2)*100-350px)]'
                  ref={messageContentDom}
                >
                  {store.selectedMessage.selected.map((item, i) => (
                    <Fragment key={i}>
                      {!item.isFromMe && (
                        <div className='w-full flex flex-col mt-[12px] items-start'>
                          <div className='w-full text-[12px] text-[#808080] text-center'>{item.time}</div>
                          <div className='max-w-[80%] py-[8px] px-[10px] bg-[#E9E9EB] rounded-[10px] text-[12px] text-[#000] mt-[2px] break-words'>
                            {content(item, t)}
                          </div>
                        </div>
                      )}
                      {item.isFromMe && (
                        <div className='w-full flex flex-col mt-[12px] items-end'>
                          <div className='w-full text-[12px] text-[#808080] text-center'>{item.time}</div>
                          <div className='max-w-[80%] py-[8px] px-[10px] bg-[#57C860] rounded-[10px] text-[12px] text-[#FFFFFF] mt-[2px]'>
                            {content(item, t)}
                          </div>
                        </div>
                      )}
                    </Fragment>
                  ))}
                </main>
              </>
            ) : (
              <NoData />
            )}
          </div>
        </Grid>
        )}
      </div>
    </>
  )
}

// TextMessages.contentHeightFixed = true

export default TextMessages


